import { useEffect, useRef, useState } from "react";

import {
  faCheck,
  faWandMagicSparkles,
} from "@fortawesome/pro-regular-svg-icons";

import { Button } from "src/design-system/Button";

const SUCCESS_DISPLAY_MS = 5000;

type AutoFillState = "idle" | "pending" | "success";

type AutoFillButtonProps = {
  /** Runs the auto-fill. Resolve `true` to show the success check. */
  onClick: () => Promise<boolean>;
  disabled?: boolean;
  dataLoc: string;
  children: React.ReactNode;
  ref?: React.Ref<HTMLButtonElement>;
};

export const AutoFillButton: React.FC<AutoFillButtonProps> = ({
  onClick,
  disabled = false,
  dataLoc,
  children,
  ref,
}) => {
  const [state, setState] = useState<AutoFillState>("idle");
  const successTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => () => clearTimeout(successTimerRef.current), []);

  const handleClick = async () => {
    if (state === "pending") {
      return;
    }

    clearTimeout(successTimerRef.current);
    setState("pending");

    // errors are the caller's responsibility — a rejected promise means no success state
    const isSuccess = await onClick().catch(() => false);
    setState(isSuccess ? "success" : "idle");

    if (isSuccess) {
      successTimerRef.current = setTimeout(
        () => setState("idle"),
        SUCCESS_DISPLAY_MS,
      );
    }
  };

  return (
    <Button
      dataLoc={dataLoc}
      htmlType="button"
      iconLeft={state === "success" ? faCheck : faWandMagicSparkles}
      isDisabled={disabled}
      isLoading={state === "pending"}
      ref={ref}
      size="sm"
      variant="secondary"
      onClick={handleClick}
    >
      {children}
    </Button>
  );
};
